import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Send, TicketIcon, Clock,
  ChevronDown, UserCheck, Lock, Unlock, MessageSquare, Paperclip, Activity, CheckCircle2,
} from "lucide-react";
import {
  useGetAdminTicketQuery,
  useAdminAddReplyMutation,
  useAdminResolveTicketMutation,
  useUpdateTicketStatusMutation,
  useAssignTicketMutation,
  TicketStatus,
} from "../api/supportApi";
import { StatusBadge, PriorityBadge, statusOptions } from "../components/TicketBadges";
import AttachmentChips from "../components/AttachmentChips";
import FileUploader from "../components/FileUploader";
import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import { useAccess } from "../../../utils/useAccess";
import { useGetAdminsQuery } from "../../admin/api/adminsApi";

function fmt(d: string) {
  return new Date(d).toLocaleString("en-NG", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function timeAgo(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AdminTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { user, can } = useAccess();
  const { data: ticket, isLoading } = useGetAdminTicketQuery(id!);
  const { data: admins = [] } = useGetAdminsQuery();
  const [addReply, { isLoading: replying }] = useAdminAddReplyMutation();
  const [adminResolve, { isLoading: resolving, error: resolveError }] = useAdminResolveTicketMutation();
  const [updateStatus, { isLoading: updatingStatus }] = useUpdateTicketStatusMutation();
  const [assignTicket, { isLoading: assigning }] = useAssignTicketMutation();

  const [body, setBody] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [statusNote, setStatusNote] = useState("");
  const [assigneeId, setAssigneeId] = useState("");

  async function handleReply(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim() || !id) return;
    try {
      await addReply({
        id,
        body: {
          body: body.trim(),
          is_internal: isInternal,
          attachments: files.length ? files : undefined,
        },
      }).unwrap();
      setBody("");
      setFiles([]);
    } catch (err: any) {
      alert(err?.data?.message ?? "Failed to send reply. Please try again.");
    }
  }

  async function handleAdminResolve() {
    if (!id) return;
    try {
      await adminResolve(id).unwrap();
    } catch (err: any) {
      alert(err?.data?.message ?? "Failed to resolve ticket. Please try again.");
    }
  }

  async function handleStatusChange(newStatus: TicketStatus) {
    if (!id) return;
    try {
      await updateStatus({ id, body: { status: newStatus, note: statusNote } }).unwrap();
      setStatusNote("");
    } catch (err: any) {
      alert(err?.data?.message ?? "Failed to update status. Please try again.");
    }
  }

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!assigneeId || !id) return;
    try {
      await assignTicket({ id, body: { assigned_to: Number(assigneeId) } }).unwrap();
      setAssigneeId("");
    } catch (err: any) {
      alert(err?.data?.message ?? "Failed to assign ticket. Please try again.");
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-5xl mx-auto p-4 md:p-6 pb-20 animate-pulse">
        <div className="h-10 bg-gray-200 rounded-xl w-32" />
        <div className="h-36 bg-white rounded-3xl border border-gray-100" />
        <div className="h-64 bg-white rounded-3xl border border-gray-100" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-red-50 border border-red-100 rounded-3xl p-8 text-center">
          <p className="text-red-600 font-black">Ticket not found</p>
        </div>
      </div>
    );
  }

  const isClosed = ticket.status === "closed";

  // Backend enforces ownership — frontend just shows a hint when clearly not assigned
  const isAssignedToMe = !!user?.id && Number(ticket.assigned_to) === Number(user.id);
  const canResolve = can.perm(PERMS.support.viewAll) || isAssignedToMe;

  const allMessages = [
    {
      id: "original",
      isOriginal: true,
      isInternal: false,
      user: ticket.user,
      body: ticket.description,
      created_at: ticket.created_at,
      isStaff: false,
      attachments: ticket.attachments ?? [],
    },
    ...(ticket.replies ?? []).map((r) => ({
      id: r.id,
      isOriginal: false,
      isInternal: r.is_internal,
      user: r.user,
      body: r.body,
      created_at: r.created_at,
      isStaff: r.user?.whois === "admin" || r.user?.whois === "executive",
      attachments: r.attachments ?? [],
    })),
  ];

  return (
    <PageGate perm={PERMS.support.adminAccess}>
      <div className="space-y-5 max-w-5xl mx-auto p-4 md:p-6 pb-20">

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm active:scale-95"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Queue
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ── Main Column ───────────────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Ticket Header */}
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center shadow-lg shadow-gray-900/20 shrink-0">
                  <TicketIcon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-2 mb-1.5">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{ticket.reference}</span>
                    {ticket.category && (
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md font-semibold">
                        {ticket.category.name}
                      </span>
                    )}
                  </div>
                  <h1 className="text-lg font-black text-gray-900 mb-3">{ticket.subject}</h1>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={ticket.status} />
                    <PriorityBadge priority={ticket.priority} />
                    <span className="flex items-center gap-1 text-[10px] text-gray-400 font-semibold">
                      <Clock className="w-3 h-3" />
                      {fmt(ticket.created_at)}
                    </span>
                    {ticket.assignee && (
                      <span className="flex items-center gap-1 text-[10px] text-gray-400 font-semibold">
                        <UserCheck className="w-3 h-3" />
                        {ticket.assignee.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Submitter info */}
            <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gray-700 flex items-center justify-center text-white text-xs font-black">
                {ticket.user?.name?.[0]?.toUpperCase() ?? "U"}
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">{ticket.user?.name ?? "Unknown"}</p>
                <p className="text-xs text-gray-400">{ticket.user?.email} · {ticket.user?.whois}</p>
              </div>
            </div>

            {/* Conversation Thread */}
            <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-50 bg-gray-50 flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Conversation · {allMessages.length} message{allMessages.length !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="px-6 py-2">
                {allMessages.map((msg, idx) => {
                  const isLast  = idx === allMessages.length - 1;
                  const initial = msg.user?.name?.[0]?.toUpperCase() ?? (msg.isStaff ? "S" : "U");

                  return (
                    <div
                      key={String(msg.id)}
                      className={`flex gap-4 py-5 ${msg.isInternal ? "opacity-90" : ""}`}
                    >
                      {/* Avatar + timeline */}
                      <div className="flex flex-col items-center shrink-0" style={{ width: 36 }}>
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-black shrink-0 ${
                            msg.isOriginal
                              ? "bg-gray-900"
                              : msg.isInternal
                              ? "bg-amber-500"
                              : msg.isStaff
                              ? "bg-indigo-600"
                              : "bg-gray-500"
                          }`}
                        >
                          {initial}
                        </div>
                        {!isLast && <div className="flex-1 w-px bg-gray-100 mt-2" />}
                      </div>

                      {/* Content */}
                      <div className={`flex-1 min-w-0 ${msg.isInternal ? "bg-amber-50/60 rounded-2xl p-3 -ml-1" : ""}`}>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-sm font-black text-gray-800">
                            {msg.user?.name ?? (msg.isStaff ? "Support Team" : "Member")}
                          </span>
                          {msg.isOriginal && (
                            <span className="text-[9px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md font-black uppercase tracking-wider">
                              Original
                            </span>
                          )}
                          {msg.isInternal && (
                            <span className="text-[9px] bg-amber-500 text-white px-2 py-0.5 rounded-md font-black uppercase tracking-wider">
                              Internal Note
                            </span>
                          )}
                          {!msg.isOriginal && !msg.isInternal && msg.isStaff && (
                            <span className="text-[9px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md font-black uppercase tracking-wider">
                              Staff
                            </span>
                          )}
                          <span className="text-[10px] text-gray-400 ml-auto font-medium" title={fmt(msg.created_at)}>
                            {timeAgo(msg.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                        <AttachmentChips attachments={msg.attachments} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Reply Box */}
            {!isClosed ? (
              <form onSubmit={handleReply} className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-50 bg-gray-50 flex items-center justify-between">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reply</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="w-3.5 h-3.5 accent-amber-500"
                    />
                    <span className="text-[10px] font-black text-amber-600 uppercase tracking-wider">Internal note</span>
                  </label>
                </div>

                <div className="p-5 space-y-4">
                  <div className="relative">
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      rows={4}
                      required
                      placeholder={isInternal ? "Internal note — not visible to the member…" : "Reply to member…"}
                      className={`w-full px-4 py-3 border rounded-2xl text-sm focus:outline-none focus:ring-2 resize-none transition placeholder-gray-400 ${
                        isInternal
                          ? "border-amber-200 bg-amber-50/60 focus:ring-amber-400"
                          : "border-gray-200 focus:ring-gray-900"
                      }`}
                    />
                  </div>

                  {/* Attachment uploader */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                      <Paperclip className="w-3 h-3" />
                      Attachments <span className="font-normal normal-case tracking-normal">(optional)</span>
                    </p>
                    <FileUploader files={files} onChange={setFiles} maxFiles={5} disabled={replying} />
                  </div>

                  <button
                    type="submit"
                    disabled={replying || !body.trim()}
                    className={`flex items-center gap-2 px-5 py-2.5 text-white text-sm font-black rounded-2xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 ${
                      isInternal
                        ? "bg-amber-500 hover:bg-amber-600 shadow-amber-500/20"
                        : "bg-gray-900 hover:bg-gray-800 shadow-gray-900/20"
                    }`}
                  >
                    <Send className="w-4 h-4" />
                    {replying ? "Sending…" : isInternal ? "Save Note" : "Send Reply"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-gray-200 flex items-center justify-center shrink-0">
                  <Lock className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-sm font-black text-gray-600">Ticket Closed</p>
                  <p className="text-xs text-gray-400 mt-0.5">Reopen to allow new replies.</p>
                </div>
              </div>
            )}

            {/* Audit Trail */}
            {(ticket.history ?? []).length > 0 && (
              <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-50 bg-gray-50 flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Activity History</span>
                </div>
                <div className="p-5 space-y-3">
                  {(ticket.history ?? []).map((h) => (
                    <div key={h.id} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-600 leading-snug">
                          <span className="font-black">{h.actor?.name ?? "System"}</span>{" "}
                          {h.action}
                          {h.from_value && h.to_value && (
                            <> from <span className="font-bold">{h.from_value}</span> to <span className="font-bold">{h.to_value}</span></>
                          )}
                          {h.note && <span className="italic text-gray-400"> — {h.note}</span>}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{fmt(h.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar Actions ───────────────────────────────────────────── */}
          <div className="space-y-4">

            {/* Status Change */}
            <AccessGate perm={PERMS.support.manage}>
              <div className="bg-white border border-gray-100 rounded-3xl shadow-sm p-5 space-y-3">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <ChevronDown className="w-3.5 h-3.5" />
                  Update Status
                </h3>
                <input
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder="Optional note…"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
                />
                <div className="space-y-2">
                  {statusOptions().map((s) => (
                    <button
                      key={s.value}
                      disabled={updatingStatus || ticket.status === s.value}
                      onClick={() => handleStatusChange(s.value)}
                      className={`w-full text-left px-4 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-50 ${
                        ticket.status === s.value
                          ? "bg-gray-900 text-white"
                          : "bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {ticket.status === s.value ? "✓ " : ""}{s.label}
                    </button>
                  ))}
                </div>
              </div>
            </AccessGate>

            {/* Assign */}
            <AccessGate perm={PERMS.support.assign}>
              <form onSubmit={handleAssign} className="bg-white border border-gray-100 rounded-3xl shadow-sm p-5 space-y-3">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <UserCheck className="w-3.5 h-3.5" />
                  Assign Ticket
                </h3>
                {ticket.assignee && (
                  <p className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                    Currently: <span className="font-bold text-gray-700">{ticket.assignee.name}</span>
                  </p>
                )}
                <select
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition bg-white"
                >
                  <option value="">— Select admin —</option>
                  {admins
                    .filter((a) => a.status === "active")
                    .map((a) => (
                      <option key={a.user_id} value={a.user_id}>
                        {a.firstname} {a.lastname}
                        {a.designation?.name ? ` · ${a.designation.name}` : ""}
                      </option>
                    ))}
                </select>
                <button
                  type="submit"
                  disabled={assigning || !assigneeId}
                  className="w-full py-2.5 bg-gray-900 text-white text-xs font-bold rounded-xl hover:bg-gray-700 transition disabled:opacity-50"
                >
                  {assigning ? "Assigning…" : "Assign"}
                </button>
              </form>
            </AccessGate>

            {/* Ticket Info */}
            <div className="bg-white border border-gray-100 rounded-3xl shadow-sm p-5 space-y-3">
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ticket Info</h3>
              {[
                { label: "Reference",   value: ticket.reference },
                { label: "Category",    value: ticket.category?.name ?? "—" },
                { label: "Priority",    value: ticket.priority },
                { label: "Created",     value: fmt(ticket.created_at) },
                { label: "Last Update", value: fmt(ticket.updated_at) },
                ...(ticket.resolved_at ? [{ label: "Resolved", value: fmt(ticket.resolved_at) }] : []),
              ].map((row) => (
                <div key={row.label}>
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-wide block">{row.label}</span>
                  <span className="text-sm text-gray-700 font-medium capitalize">{row.value}</span>
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <AccessGate perm={PERMS.support.manage}>
              <div className="space-y-2">
                {/* Resolve — only for assigned admin or those with viewAll */}
                {ticket.status !== "resolved" && ticket.status !== "closed" && (
                  <div className="space-y-1.5">
                    <button
                      disabled={resolving || !canResolve}
                      onClick={handleAdminResolve}
                      title={!canResolve ? "You can only resolve tickets assigned to you" : undefined}
                      className="w-full flex items-center justify-center gap-2 py-3 border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-black rounded-2xl hover:bg-emerald-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {resolving ? "Resolving…" : "Mark as Resolved"}
                    </button>
                    {resolveError && (
                      <p className="text-[10px] text-red-600 text-center">
                        {(resolveError as any)?.data?.message
                          ?? (resolveError as any)?.error
                          ?? `Error ${(resolveError as any)?.status ?? (resolveError as any)?.originalStatus ?? ""}: Could not resolve ticket.`}
                      </p>
                    )}
                    {!canResolve && !resolveError && (
                      <p className="text-[10px] text-amber-600 text-center">
                        Assign ticket to yourself first
                      </p>
                    )}
                  </div>
                )}

                {ticket.status !== "closed" ? (
                  <button
                    disabled={updatingStatus}
                    onClick={() => handleStatusChange("closed")}
                    className="w-full flex items-center justify-center gap-2 py-3 border border-gray-200 bg-white text-gray-700 text-sm font-bold rounded-2xl hover:bg-gray-50 transition disabled:opacity-50"
                  >
                    <Lock className="w-4 h-4" />
                    Close Ticket
                  </button>
                ) : (
                  <button
                    disabled={updatingStatus}
                    onClick={() => handleStatusChange("open")}
                    className="w-full flex items-center justify-center gap-2 py-3 border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm font-black rounded-2xl hover:bg-indigo-100 transition disabled:opacity-50"
                  >
                    <Unlock className="w-4 h-4" />
                    Reopen Ticket
                  </button>
                )}
              </div>
            </AccessGate>
          </div>
        </div>
      </div>
    </PageGate>
  );
}
